import { Box, Chip, Divider, Typography } from '@mui/material';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import { ReactComponent as VerticalDots } from 'assets/vertical-dots.svg';
import { ReactComponent as ViewIcon } from 'assets/view.svg';
import DeleteIcon from 'assets/delete-red.png';
import { ReactComponent as EditIcon } from 'assets/edit.svg';
import React, { useState } from 'react';
import zeplinColor from 'theme/Color';
import styles from './ContactCard.module.css';
import { Contact } from 'model/contacts.model';
import { modalReducer } from 'reducers/modalReducer';
import CustomDeleteModal from 'components/shared/CustomDeleteModal/CustomDeleteModal';
import { ContactService } from 'service/contact.service';

const ContactCard = ({
  onViewDetails,
  contact,
  onDelete,
  onEdit,
}: {
  onViewDetails: (contact: Contact) => void;
  contact: Contact;
  onDelete: (contact: any, alertText: any) => void;
  onEdit: (contact: any) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLImageElement | null>(null);
  const [modalState, modalDispatch] = React.useReducer(modalReducer, { delete: false });
  const handleClick = (event: React.MouseEvent<HTMLImageElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const handleDeleteClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    modalDispatch({ type: 'OPEN', payload: 'delete' });
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await ContactService.deleteContact(contact.contactId!);
      modalDispatch({ type: 'CLOSE', payload: 'delete' });
      onDelete('delete', 'Contact deleted successfully');
    } catch (error) {
      onDelete('delete', 'Failed to delete contact');
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        border: `1px solid ${zeplinColor.Neutral5}`,
        borderRadius: '14px',
        display: 'flex',
        gap: '20px',
        padding: '16px',
      }}
    >
      <Box sx={{ display: 'flex', gap: '12px', width: '100%', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', gap: '12px', width: '100%' }}>
          <img
            src='/assets/captive8.svg'
            alt='contact'
            style={{ width: '52px', height: '52px', borderRadius: '48px' }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Typography
                variant='responsiveHeading4'
                sx={{
                  color: zeplinColor.BasicBlack,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: { xs: '5rem', xl: '10rem' },
                  position: 'relative',
                }}
              >
                {contact.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <Chip
                  label={
                    <Typography
                      variant='responsiveLabel1'
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      Active
                    </Typography>
                  }
                  sx={{
                    bgcolor: zeplinColor.Success,
                    color: zeplinColor.Neutral1,
                    // width: '39px',
                    '&.MuiChip-root': {
                      // height: '22px',
                      padding: '4px 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: { xs: '18px', xl: '22px' }, // 18px below 1700px, 22px above
                      // padding: {  '4px 8px' }, // Smaller padding below 1700px
                      borderRadius: '50px',
                    },
                    '& .MuiChip-label': {
                      padding: 0,
                    },
                  }}
                />

                <LightTooltip
                  title={
                    <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                      Options
                    </Typography>
                  }
                  placement='bottom-end'
                  style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                >
                  <VerticalDots
                    className={styles['vertical-icon']}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      handleClick(e);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                </LightTooltip>
                <CustomPopOver
                  onClick={(e: any) => {
                    e.stopPropagation();
                  }}
                  setAnchorEl={setAnchorEl}
                  anchorEl={anchorEl}
                  id={id}
                  sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      padding: '16px',
                    }}
                  >
                    {' '}
                    <Box
                      sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
                      onClick={() => {
                        onViewDetails(contact);
                        setAnchorEl(null);
                      }}
                    >
                      {/* <img src={'/assets/view.svg'} width={18} height={18} /> */}
                      <ViewIcon width={18} height={18} />
                      <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                        View
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(contact);
                        setAnchorEl(null);
                      }}
                    >
                      <EditIcon width={18} height={18} />
                      {/* <DeleteIcon width={18} height={18} /> */}
                      <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                        Edit
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(e);
                      }}
                    >
                      <img src={DeleteIcon} width={18} height={18} />
                      {/* <DeleteIcon width={18} height={18} /> */}
                      <Typography variant='responsiveBody3' color={zeplinColor.Error}>
                        Delete
                      </Typography>
                    </Box>
                  </Box>
                </CustomPopOver>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Typography variant='responsiveBody3'>{contact.roles}</Typography>
              <Box
                sx={{
                  height: '16px',
                  width: '1px',
                  backgroundColor: zeplinColor.Neutral5,
                  display: 'inline-block',
                  marginX: '4px', // Add some margin on both sides
                }}
              />
              <Typography variant='responsiveBody3'>{contact.company}</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <img src='/assets/phone-icon.svg' alt='Contact' width={'20px'} height={'20px'} />
          <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
            {contact.phone}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <img src='/assets/mail-dark-icon.svg' alt='Contact' width={'20px'} height={'20px'} />
          <Typography
            component='a'
            href={contact.email ? `mailto:${contact.email}` : undefined}
            onClick={(e) => e.stopPropagation()}
            variant='responsiveBody3'
            sx={{
              color: zeplinColor.Brand1,
              cursor: contact.email ? 'pointer' : 'default',
              textDecoration: 'none',
              '&:hover': { textDecoration: contact.email ? 'underline' : 'none' },
            }}
          >
            {contact.email}
          </Typography>
        </Box>
        <Divider component='div' sx={{ borderColor: zeplinColor.Neutral5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <img
              src='/assets/slack-user.svg'
              alt='Contact'
              width={'28px'}
              height={'28px'}
              style={{ borderRadius: '22px' }}
            />
            <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
              {contact?.createdByName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <img src='/assets/clock-01.svg' alt='Contact' width={'20px'} height={'20px'} />
            <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
              {contact?.createdAt
                ? new Date(contact.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : 'N/A'}
            </Typography>
          </Box>
        </Box>
      </Box>
      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={handleConfirmDelete}
        title='Delete Contact'
        description='Are you sure you want to delete this contact? This action cannot be undone.'
      />
    </Box>
  );
};

export default ContactCard;
